/**
 * Entry point for the nc-core IIFE bundle (window.NC). Everything exported
 * here is a public seam the monolith pages delegate into — treat renames as
 * breaking changes to static/*.html.
 */
export { CHARGE_RATES, computeCharges, tradeNetPnl } from "./charges";
export type { ChargeBreakdown, ChargeRates, Side } from "./charges";

import * as ws from "./ws";
export { ws };

import * as marketTime from "./market_time";
export { marketTime };

import * as session from "./domain/session";
export { session };

import * as format from "./domain/format";
export { format };

import * as geometry from "./drawings/geometry";
export { geometry };

import { wireBacktestPanel, applyEquityChartTheme } from "./panels/backtest";
import { initAlertsPanel } from "./panels/alerts";
import { initSymbolSearch } from "./panels/symbol_search";
export const panels = { wireBacktestPanel, applyEquityChartTheme, initAlertsPanel, initSymbolSearch };

import { initMoreMenu } from "./ui/more_menu";
import * as nav from "./ui/nav";
import * as accountMenu from "./ui/account_menu";
export const ui = { initMoreMenu, nav, accountMenu };
export { nav, accountMenu };

import * as health from "./health";
export { health };

import * as http from "./core/http";
export { http };

import * as chartSettings from "./chart/settings";
export { chartSettings };

import * as layoutManager from "./chart/layout_manager";
export { layoutManager };

import * as optionChain from "./panels/option_chain";
export { optionChain };

import * as prefs from "./ui/prefs";
export { prefs };

import * as shapes from "./drawings/shapes";
export { shapes };

import * as magnet from "./drawings/magnet";
export { magnet };

import * as drawingHistory from "./drawings/history";
export { drawingHistory };

import * as lots from "./ui/lots";
export { lots };

import * as hitTest from "./drawings/hit_test";
export { hitTest };

import * as ordersTrades from "./live/orders_trades";
export { ordersTrades };

import * as takeHome from "./live/take_home";
export { takeHome };

import * as popupPosition from "./drawings/popup_position";
export { popupPosition };

import * as timeframes from "./chart/timeframes";
export { timeframes };

import * as legend from "./chart/legend";
export { legend };

import * as drawingColors from "./drawings/colors";
export { drawingColors };

import * as tickAge from "./live/tick_age";
export { tickAge };

import * as lineStyles from "./drawings/line_styles";
export { lineStyles };

import * as positionHelpers from "./live/position_helpers";
export { positionHelpers };

import * as htmlUtils from "./ui/html_utils";
export { htmlUtils };

import * as sessionSeparators from "./chart/session_separators";
export { sessionSeparators };

import * as coordinateExtrapolation from "./chart/coordinate_extrapolation";
export { coordinateExtrapolation };

import * as fibonacci from "./drawings/fibonacci";
export { fibonacci };

import * as handles from "./drawings/handles";
export { handles };

import * as adaptiveLoop from "./core/adaptive_loop";
export { adaptiveLoop };

import * as toast from "./ui/toast";
export { toast };

import * as indicatorThrottle from "./chart/indicator_throttle";
export { indicatorThrottle };

import * as annotations from "./drawings/annotations";
export { annotations };

import * as marginPreview from "./live/margin_preview";
export { marginPreview };

import * as dragEngine from "./drawings/drag_engine";
export { dragEngine };

import * as positionExit from "./live/position_exit";
export { positionExit };

import * as hotkeys from "./live/hotkeys";
export { hotkeys };

import * as priceLines from "./drawings/price_lines";
export { priceLines };

import * as paperFormat from "./paper/format";
export { paperFormat };

import * as indicatorSettingsUi from "./indicators/settings_ui";
export { indicatorSettingsUi };

import * as symbols from "./symbols";
export { symbols };

import * as paperPositions from "./paper/positions";
export { paperPositions };

import * as paperMargin from "./paper/margin";
export { paperMargin };

import * as paperFunds from "./paper/funds";
export { paperFunds };

import * as paperRisk from "./paper/risk";
export { paperRisk };

import * as paperOrderBook from "./paper/order_book";
export { paperOrderBook };

import * as paperPositionsTable from "./paper/positions_table";
export { paperPositionsTable };

import * as paperOptionsToolbar from "./paper/options_toolbar";
export { paperOptionsToolbar };

import * as tabLease from "./paper/tab_lease";
export { tabLease };

import * as paperTradingState from "./paper/trading_state";
export { paperTradingState };

import * as positionClosure from "./paper/position_closure";
export { positionClosure };

import * as paperOrderTicket from "./paper/order_ticket";
export { paperOrderTicket };

import * as tempPreview from "./drawings/temp_preview";
export { tempPreview };

import * as popupControls from "./drawings/popup_controls";
export { popupControls };

import * as renderCalc from "./drawings/render_calc";
export { renderCalc };

// Long/Short Position (risk-reward) PLANNING drawing — not broker positions. Named
// positionTool* (drawing type `rr_position`), never `position*`, so it cannot be
// confused with positionExit / positionClosure / positionTableRows in this bundle.
import * as positionTool from "./drawings/position_tool";
export { positionTool };

import * as positionToolRender from "./drawings/position_tool_render";
export { positionToolRender };

// Anchored VWAP maths (DRAW-AVWAP-1): one implementation shared by the renderer and the hit-tester.
import * as anchoredVwap from "./drawings/anchored_vwap";
export { anchoredVwap };

import * as oiProfile from "./indicators/oi_profile";
export { oiProfile };

import * as multiplexClient from "./live/multiplex_client";
export { multiplexClient };

import * as positionTableRows from "./live/position_table_rows";
export { positionTableRows };

import * as mtmCalc from "./live/mtm_calc";
export { mtmCalc };

import * as hedgePlanner from "./live/hedge_planner";
export { hedgePlanner };

import * as fundsView from "./live/funds_view";
export { fundsView };

import * as chartReplay from "./chart/replay";
export { chartReplay };

import * as basket from "./basket/leg";
export { basket };

import * as basketPayoff from "./basket/payoff";
export { basketPayoff };

import * as basketPersist from "./basket/persist";
export { basketPersist };

import * as basketExecute from "./basket/execute";
export { basketExecute };

import * as livePricing from "./live/pricing";
export { livePricing };

import * as livePositions from "./live/position_model";
export { livePositions };

import * as liveContractMatch from "./live/contract_match";
export { liveContractMatch };

import * as liveStrikeSelection from "./live/strike_selection";
export { liveStrikeSelection };

import * as brokerCredentials from "./live/broker_credentials";
export { brokerCredentials };

export { createStore, bridgeFields } from "./store";
export type { Store } from "./store";

import * as panes from "./panes";
export { panes };

export { createCasSync } from "./server_sync";
export type { CasSync, CasSyncOpts, CasSaveResult, CasLoadResult } from "./server_sync";

export { createPaperApi, PaperApiError } from "./paper_api";
export type { PaperApi, PaperHttp, PlaceOrderReq, ClosePositionReq, PaperApiResult } from "./paper_api";

import * as quickOrder from "./live/quick_order";
export { quickOrder };

import * as paperSettingsView from "./paper/settings_view";
export { paperSettingsView };

import * as archivePicker from "./panels/archive_picker";
export { archivePicker };

